// Admin Panel Cloud Files - Implementare completă
import React, { useState, useEffect } from 'react';
import {
  Cloud,
  Folder,
  File,
  Upload,
  Download,
  Settings,
  Search,
  Filter,
  Grid,
  List,
  MoreHorizontal,
  Star,
  Share2,
  Trash2,
  RefreshCw,
  FileText,
  Image,
  Video,
  Archive,
  Music,
  Code,
  Eye,
  Edit,
  Copy,
  Move,
  FolderPlus,
  FilePlus,
  Calendar,
  User,
  HardDrive,
  AlertCircle,
  CheckCircle2,
  Clock
} from 'lucide-react';

const CloudFiles = () => {
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState(['Root']);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Mock data pentru fișiere și foldere
  const [files, setFiles] = useState([
    {
      id: 1,
      name: 'Documente Contracte',
      type: 'folder',
      size: null,
      modified: '2025-01-15 14:30',
      owner: 'Admin',
      items: 47,
      starred: false
    },
    {
      id: 2,
      name: 'Imagini Marketing',
      type: 'folder',
      size: null,
      modified: '2025-01-14 11:22',
      owner: 'Marketing Team',
      items: 156,
      starred: true
    },
    {
      id: 3,
      name: 'Contract_Client_ABC_2025.pdf',
      type: 'pdf',
      size: '2.4 MB',
      modified: '2025-01-15 16:45',
      owner: 'Legal Team',
      starred: false
    },
    {
      id: 4,
      name: 'Prezentare_Produse.pptx',
      type: 'presentation',
      size: '15.7 MB',
      modified: '2025-01-15 13:20',
      owner: 'Sales Team',
      starred: false
    },
    {
      id: 5,
      name: 'Raport_Financiar_Q1.xlsx',
      type: 'spreadsheet',
      size: '890 KB',
      modified: '2025-01-14 17:15',
      owner: 'Accounting',
      starred: true
    },
    {
      id: 6,
      name: 'Logo_Companie_HD.png',
      type: 'image',
      size: '3.2 MB',
      modified: '2025-01-13 10:30',
      owner: 'Design Team',
      starred: false
    },
    {
      id: 7,
      name: 'Video_Tutorial_Produs.mp4',
      type: 'video',
      size: '245 MB',
      modified: '2025-01-12 14:45',
      owner: 'Marketing Team',
      starred: false
    },
    {
      id: 8,
      name: 'Backup_Database_Jan2025.zip',
      type: 'archive',
      size: '1.2 GB',
      modified: '2025-01-11 23:30',
      owner: 'IT Department',
      starred: true
    }
  ]);

  const [stats] = useState({
    totalFiles: 1247,
    totalFolders: 89,
    totalSize: '45.7 GB',
    usedSpace: 76, // percentage
    recentUploads: 23,
    sharedFiles: 156
  });

  // Funcții utile
  const getFileIcon = (type) => {
    const iconProps = { className: 'w-5 h-5' };
    
    switch (type) {
      case 'folder': return <Folder {...iconProps} className="w-5 h-5 text-blue-500" />;
      case 'pdf': return <FileText {...iconProps} className="w-5 h-5 text-red-500" />;
      case 'image': return <Image {...iconProps} className="w-5 h-5 text-green-500" />;
      case 'video': return <Video {...iconProps} className="w-5 h-5 text-purple-500" />;
      case 'archive': return <Archive {...iconProps} className="w-5 h-5 text-orange-500" />;
      case 'music': return <Music {...iconProps} className="w-5 h-5 text-pink-500" />;
      case 'code': return <Code {...iconProps} className="w-5 h-5 text-gray-500" />;
      case 'presentation': return <FileText {...iconProps} className="w-5 h-5 text-blue-500" />;
      case 'spreadsheet': return <FileText {...iconProps} className="w-5 h-5 text-green-600" />;
      default: return <File {...iconProps} className="w-5 h-5 text-gray-400" />;
    }
  };

  const toggleFileSelection = (fileId) => {
    setSelectedFiles(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedFiles = [...filteredFiles].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];
    
    if (sortBy === 'size') {
      // Convert size to bytes for proper sorting
      aValue = a.size ? parseFloat(a.size) : 0;
      bValue = b.size ? parseFloat(b.size) : 0;
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Cloud className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Fișiere & Documente Cloud
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Gestionează toate fișierele și documentele din Amazon S3
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => setShowUploadModal(true)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <File className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalFiles.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Fișiere</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Folder className="w-8 h-8 text-orange-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalFolders}</p>
                <p className="text-xs text-gray-500">Foldere</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <HardDrive className="w-8 h-8 text-green-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalSize}</p>
                <p className="text-xs text-gray-500">Spațiu total</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Upload className="w-8 h-8 text-purple-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.recentUploads}</p>
                <p className="text-xs text-gray-500">Upload-uri noi</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Share2 className="w-8 h-8 text-indigo-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.sharedFiles}</p>
                <p className="text-xs text-gray-500">Partajate</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="w-8 h-8 mr-3">
                <div className="relative w-8 h-8">
                  <svg className="w-8 h-8 transform -rotate-90" viewBox="0 0 32 32">
                    <circle cx="16" cy="16" r="12" stroke="currentColor" strokeWidth="4" fill="transparent" className="text-gray-300" />
                    <circle cx="16" cy="16" r="12" stroke="currentColor" strokeWidth="4" fill="transparent" 
                            strokeDasharray={`${stats.usedSpace * 0.75} 75`} className="text-red-500" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-bold text-gray-900 dark:text-white">{stats.usedSpace}%</span>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Spațiu folosit</p>
                <p className="text-xs text-gray-500">din 60 GB</p>
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            
            {/* Breadcrumb */}
            <div className="flex items-center text-sm">
              {currentPath.map((path, index) => (
                <div key={index} className="flex items-center">
                  <button className="text-blue-600 hover:text-blue-700 font-medium">
                    {path}
                  </button>
                  {index < currentPath.length - 1 && (
                    <span className="mx-2 text-gray-400">/</span>
                  )}
                </div>
              ))}
            </div>

            {/* Search and Actions */}
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Caută fișiere..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-64"
                />
              </div>

              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="name">Nume</option>
                <option value="modified">Data modificării</option>
                <option value="size">Dimensiune</option>
                <option value="type">Tip</option>
              </select>

              <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white dark:bg-gray-600 shadow-sm' : ''}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-white dark:bg-gray-600 shadow-sm' : ''}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>

              <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Files Grid/List */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {sortedFiles.map((file) => (
                <div
                  key={file.id}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all hover:shadow-md ${
                    selectedFiles.includes(file.id)
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                  }`}
                  onClick={() => toggleFileSelection(file.id)}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-3">
                      {getFileIcon(file.type)}
                    </div>
                    <h3 className="font-medium text-gray-900 dark:text-white text-sm mb-1 truncate w-full">
                      {file.name}
                    </h3>
                    {file.size && (
                      <p className="text-xs text-gray-500 mb-1">{file.size}</p>
                    )}
                    {file.items && (
                      <p className="text-xs text-gray-500 mb-1">{file.items} elemente</p>
                    )}
                    <p className="text-xs text-gray-400">{file.modified}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {sortedFiles.map((file) => (
                <div
                  key={file.id}
                  className={`flex items-center p-3 rounded-lg cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-gray-700 ${
                    selectedFiles.includes(file.id)
                      ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-500'
                      : 'border border-transparent'
                  }`}
                  onClick={() => toggleFileSelection(file.id)}
                >
                  <div className="flex items-center flex-1 min-w-0">
                    <div className="mr-4">
                      {getFileIcon(file.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 dark:text-white truncate">
                        {file.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Modificat: {file.modified} • {file.owner}
                      </p>
                    </div>
                    <div className="ml-4 text-right">
                      {file.size && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">{file.size}</p>
                      )}
                      {file.items && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">{file.items} elemente</p>
                      )}
                    </div>
                    <div className="ml-4">
                      <button className="p-1 text-gray-400 hover:text-gray-600 rounded">
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected Files Actions */}
        {selectedFiles.length > 0 && (
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-6 py-3 rounded-xl shadow-lg">
            <div className="flex items-center space-x-4">
              <span className="font-medium">{selectedFiles.length} fișiere selectate</span>
              <div className="flex items-center space-x-2">
                <button className="p-2 hover:bg-gray-800 rounded-lg transition-colors">
                  <Download className="w-4 h-4" />
                </button>
                <button className="p-2 hover:bg-gray-800 rounded-lg transition-colors">
                  <Share2 className="w-4 h-4" />
                </button>
                <button className="p-2 hover:bg-gray-800 rounded-lg transition-colors">
                  <Move className="w-4 h-4" />
                </button>
                <button className="p-2 hover:bg-red-700 rounded-lg transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Upload Fișiere
              </h3>
              <button 
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                Drag & drop fișierele aici
              </p>
              <p className="text-sm text-gray-500 mb-4">sau</p>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
                Selectează Fișierele
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CloudFiles;